import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { io } from "socket.io-client";
import styled, { keyframes, css } from "styled-components";
import Picker from "emoji-picker-react";
import decodeJWT from "../../utils/jwt_helper";
import Loader from "../../components/Loader";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import "./Chat.css";
import AuthContext from "../../contexts/AuthProvider";

import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

// Keyframes for loading spinner animation
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const ChatContainer = styled.div`
  display: flex;
  height: 100vh;
  font-family: "Inter", sans-serif;
`;

const Sidebar = styled.div`
  width: 27%;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  padding: 20px;
  border-bottom: 1px solid #e1e4e8;
  font-weight: bold;
  font-size: 18px;
  color: #1a1a1a;
`;

const EmptyChatState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #6b7280;
`;

const SearchBox = styled.input`
  padding: 10px 0;
  width: 80%;
  &:focus-visible {
    outline: none;
  }
`;

const ContactsList = styled.div`
  flex: 1;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 10px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #b0b0b0;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 10px 15px;
`;

const Button = styled.button`
  padding: 8px 12px;
  border: none;
  background-color: #6609f8;
  color: white;
  border-radius: 5px;
  cursor: pointer;
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ContactItem = styled.div`
  padding: 8px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  background: #ffffff;
  border: 1px solid ${props => props.active ? '#6609f8' : 'var(--Primary-dark2, transparent)'};
  transition: 0.2s all ease-in-out;
  margin-bottom: 8px;
  border-radius: 8px;
  position: relative;
  background-color: ${props => props.active ? '#f4f1ff' : '#ffffff'};
  
  &:hover {
    border-color: #6609f8;
  }
`;

const Avatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #bdbfbe;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  background-image: ${(props) =>
    props.image ? `url(${props.image})` : "none"};
  background-size: cover;
  background-position: center;
  border: 1.5px solid var(--Black-Header3, #888888);
`;

const ChatArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ChatBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
`;

const MessageWrapper = styled.div`
  display: flex;
  justify-content: ${(props) => (props.sent ? "flex-end" : "flex-start")};
  margin-bottom: 10px;
`;

const MessageBubble = styled.div`
  max-width: 100%;
  width: 100%;
  padding: 12px 15px;
  border-radius: 18px;
  background-color: ${(props) => (props.sent ? "#FEF4EE" : "#ffffff")};
  color: ${(props) => (props.sent ? "#464646" : "#464646")};
  position: relative;
`;

const ChatFooter = styled.div`
  display: flex;
  position: relative;
  background: transparent;
  margin-top: 18px;
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 12px 15px 12px 0;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  &:focus-visible {
    outline: none;
  }
`;

const ContactInfo = styled.div`
  flex: 1;
`;

const ContactName = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 100%;
  color: #292929;
`;

const SendButton = styled.button`
  margin-right: 10px;
  border-radius: 8px;
  padding: 12px;
  background: var(--Primary-light2, #f4f1ff);
  border: 1px solid var(--Primary-dark2, #6609f8);
  width: 48px;
  height: 48px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0066dd;
  }

  &:disabled {
    background-color: rgb(233, 227, 255);
    cursor: not-allowed;
  }
`;

const StatusIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: #4caf50;
  display: ${(props) => (props.online ? "block" : "none")};
  margin-left: 10px;
  position: absolute;
  bottom: 0;
  right: 0;
  border: 2px solid white;
`;

const AvatarWrapper = styled.div`
  position: relative;
`;

const MessageStatus = styled.div`
  font-size: 0.7rem;
  margin-top: 5px;
  text-align: right;
  color: ${(props) =>
    props.sent ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)"};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 4px;
`;

const TypingIndicator = styled.div`
  padding: 8px 12px;
  background-color: #f0f2f5;
  border-radius: 18px;
  align-self: flex-start;
  margin-bottom: 10px;
  font-size: 0.9rem;
  color: #666;
  display: ${(props) => (props.show ? "block" : "none")};
  animation: fadeIn 0.3s;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ContactStatus = styled.div`
  font-size: 12px;
  color: ${(props) => (props.isOnline ? "green" : "#ff4d4f")};
`;

const FileButton = styled.label`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  color: #6b7280;

  &:hover {
    background: #f3f4f6;
    transform: scale(1.1);
    color: #374151;
  }

  &:active {
    transform: scale(0.95);
  }

  input {
    display: none;
  }
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  margin-left: 10px;
  padding: 8px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  order: 2;

  button {
    margin-left: 8px;
    background: none;
    border: none;
    cursor: pointer;
    color: #666;
    font-size: 16px;

    &:hover {
      color: #000;
    }
  }
`;

// Spinner components using css helper for keyframe interpolation
const SmallSpinner = styled.div`
  width: 12px;
  height: 12px;
  border: 2px solid #666;
  border-top: 2px solid transparent;
  border-radius: 50%;
  margin-right: 6px;
  ${css`
    animation: ${spin} 1s linear infinite;
  `}
`;

const SendButtonSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-top: 2px solid transparent;
  border-radius: 50%;
  ${css`
    animation: ${spin} 1s linear infinite;
  `}
`;

const VenueOwnerChatSystem = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderId = location.state?.orderId; // Get orderId from navigation state
  const { t, i18n } = useTranslation("chat");
  const lang = i18n.language;
  const [socket, setSocket] = useState(null);
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [contacts, setContacts] = useState({ users: [] });
  const [activeContact, setActiveContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentContactPage, setCurrentContactPage] = useState(1);
  const [totalContactPages, setTotalContactPages] = useState(1);
  const [currentMessagePage, setCurrentMessagePage] = useState(1);
  const [totalMessagePages, setTotalMessagePages] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasContacts, setHasContacts] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null); // Add selectedFile state
  const [isUploading, setIsUploading] = useState(false); // Add upload loading state
  const [pendingCustomerId, setPendingCustomerId] = useState(null); // Store customer ID to auto-select
  const typingTimeoutRef = useRef(null);
  const messagesEndRef = useRef(null);
  const chatBodyRef = useRef(null);
  const socketRef = useRef(null);
  const { isAuthenticated } = useContext(AuthContext);
  const userData = localStorage.getItem("user");
  const parsedUser = userData ? JSON.parse(userData) : null;

console.log("Orderid:", orderId); // Debug log

  // File handling utility functions
  const getFileIcon = (filename) => {
    if (!filename) return "📎";

    const extension = filename.split(".").pop()?.toLowerCase();

    switch (extension) {
      case "jpg":
      case "jpeg":
      case "png":
      case "gif":
      case "webp":
        return "🖼️";
      case "pdf":
        return "📄";
      case "doc":
      case "docx":
        return "📝";
      case "mp4":
      case "avi":
      case "mov":
      case "mkv":
        return "🎥";
      case "mp3":
      case "wav":
      case "flac":
        return "🎵";
      case "zip":
      case "rar":
      case "7z":
        return "📦";
      default:
        return "📎";
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "";
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
  };

  // Upload file via HTTP API
  const uploadFile = async (file) => {
    try {
      const formData = new FormData();
      formData.append("chat-file", file);

      const response = await fetch(`${baseUrl}/api/v1/chat/upload`, {
        method: "POST",
        body: formData,
        credentials: "include", // Include cookies for authentication
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Upload failed");
      }

      const data = await response.json();
      return data.filename;
    } catch (error) {
      console.error("File upload error:", error);
      throw error;
    }
  };

  // Check authentication state
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // Handle pending customer selection when contacts are updated
  useEffect(() => {
    if (pendingCustomerId && contacts.users.length > 0) {
      const customer = contacts.users.find(user => user.id === pendingCustomerId);
      if (customer) {
        console.log("Auto-selecting pending customer:", customer.name);
        handleSelectContact(customer);
        setPendingCustomerId(null); // Clear pending selection
      }
    }
  }, [contacts.users, pendingCustomerId]);

  // useEffect to handle orderId from URL params
  useEffect(() => {
    if (socket && orderId) {
      console.log("Order ID found in URL params:", orderId);
      
      // Emit event to get customer details by order ID
      socket.emit("get_customer_by_order_id", { order_id: orderId });
    }
  }, [socket, orderId]);

  // Handle clicking outside emoji picker
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showPicker && !event.target.closest('.emojy-picker') && !event.target.closest('[aria-label="Open emoji picker"]')) {
        setShowPicker(false);
      }
    };

    if (showPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showPicker]);

  const handleError = useCallback(
    (error) => {
      console.error("Error:", error);
      if (error?.status === 401 || error?.error === "has expired") {
        navigate("/login");
      }
    },
    [navigate]
  );

  // Initialize socket connection with reconnection logic
  const initializeSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const newSocket = io(baseUrl, {
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      autoConnect: true,
      transports: ["websocket"],
      withCredentials: true,
      query: {
        role: "venue_owner",
        lang: lang,
      },
    });

    newSocket.on("connect", () => {
      console.log("Socket connected");
      setSocket(newSocket);
      socketRef.current = newSocket;
    });

    newSocket.on("disconnect", (reason) => {
      console.log("Socket disconnected:", reason);
      if (reason === "io server disconnect") {
        newSocket.connect();
      }
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
      handleError(error);
      setTimeout(() => {
        newSocket.connect();
      }, 1000);
    });

    return newSocket;
  }, [handleError]);

  // Initialize socket on component mount
  useEffect(() => {
    const newSocket = initializeSocket();
    return () => {
      newSocket.disconnect();
      socketRef.current = null;
    };
  }, [initializeSocket]);
  useEffect(() => {
    if (!socket) return;
    console.log("socket initialized", socket);

    console.log("Socket connected, emitting venue_owner_join");
    socket.emit("venue_owner_join", { lang });
    // check success join
    socket.on("venue_owner_online", (data) => {
      console.log("Successfully joined chat:", data);
    });
    fetchUsers(currentContactPage);

    socket.on("venue_owner_join_error", (error) => {
      console.error("Authentication failed:", error);

      if (error?.error === "has expired") {
        navigate("/login");
        return;
      } else {
        console.log(error?.error || "An unknown error occurred");
      }

      socket.disconnect();
    });

    socket.on("users", ({ data, total, totalPages, currentPage }) => {
      setLoading(false);
      const updatedUsers = data.map((user) => ({
        ...user,
        isOnline: user.isOnline || false,
        lastMessageTime: user.lastMessageTime || user.last_message_time,
        lastMessage: user.lastMessage || user.last_message,
        // unreadMessages: user.unreadMessages || user.unread_messages || 0,
        type: 'user'
      }));

      // Initialize unread messages state
      // const initialUnread = {};
      // updatedUsers.forEach((user) => {
      //   if (user.unreadMessages > 0) {
      //     initialUnread[user.id] = user.unreadMessages;
      //   }
      // });
      // setUnreadMessages(initialUnread);
      const initialUnread = {};
      data.forEach((user) => {
        if (user.unreadMessages > 0 || user.unread_messages > 0) {
          initialUnread[user.id] = user.unreadMessages || user.unread_messages;
        }
      });
      setUnreadMessages(initialUnread);

      // Sort and set contacts
      // updatedUsers.sort((a, b) => {
      //   const timeA = a.lastMessageTime
      //     ? new Date(a.lastMessageTime).getTime()
      //     : 0;
      //   const timeB = b.lastMessageTime
      //     ? new Date(b.lastMessageTime).getTime()
      //     : 0;
      //   return timeB - timeA;
      // });

      // setHasContacts(updatedUsers.length > 0);
      // setContacts({ users: updatedUsers });
      // setTotalContactPages(totalPages);
      // setCurrentContactPage(currentPage);
      updatedUsers.sort((a, b) => {
        const timeA = a.lastMessageTime
          ? new Date(a.lastMessageTime).getTime()
          : 0;
        const timeB = b.lastMessageTime
          ? new Date(b.lastMessageTime).getTime()
          : 0;
        return timeB - timeA;
      });

      // Preserve any existing contacts that were added via order ID
      setContacts((prevContacts) => {
        console.log("Users socket - previous contacts:", prevContacts.users);
        console.log("Users socket - new data from server:", updatedUsers);
        
        const existingUsers = prevContacts.users || [];
        const mergedUsers = [...updatedUsers];
        
        // Add any existing users that aren't in the new data
        existingUsers.forEach((existingUser) => {
          const foundInNew = updatedUsers.find(newUser => newUser.id === existingUser.id);
          if (!foundInNew) {
            console.log("Preserving existing user not in server data:", existingUser);
            mergedUsers.unshift(existingUser); // Add to beginning to maintain order preference
          }
        });

        // Sort the final merged list
        mergedUsers.sort((a, b) => {
          const timeA = a.lastMessageTime
            ? new Date(a.lastMessageTime).getTime()
            : 0;
          const timeB = b.lastMessageTime
            ? new Date(b.lastMessageTime).getTime()
            : 0;
          return timeB - timeA;
        });

        console.log("Users socket - final merged contacts:", mergedUsers);
        
        // Update hasContacts based on final merged list
        setHasContacts(mergedUsers.length > 0);
        
        return { users: mergedUsers };
      });
      
      setTotalContactPages(totalPages);
      setCurrentContactPage(currentPage);
    });

    socket.on("user_online", ({ user_id, isOnline }) => {
      setContacts((prevContacts) => ({
        users: prevContacts.users.map((user) =>
          user.id === user_id ? { ...user, isOnline } : user
        ),
      }));
    });

    socket.on("receive_message", (newMessage) => {
      if (!newMessage || !newMessage.sender_id || !newMessage.receiver_id) {
        console.warn("Invalid message format", newMessage);
        return;
      }

      const isForCurrentUser = newMessage.receiver_id === parsedUser?.id;
      const isOurMessage = newMessage.sender_id === parsedUser?.id;
      const partnerId = isOurMessage
        ? newMessage.receiver_id
        : newMessage.sender_id;

      // Check if this message belongs to the active conversation
      const isForActiveChat =
        activeContact &&
        ((newMessage.sender_id === activeContact.id &&
          newMessage.receiver_id === parsedUser?.id) ||
          (newMessage.receiver_id === activeContact.id &&
            newMessage.sender_id === parsedUser?.id));

      // Update messages state ONLY if it's for the active chat
      if (isForActiveChat) {
    setMessages((prev) => {
      if (isOurMessage && newMessage.tempId) {
        // Replace only the temp message with matching tempId
        return prev.map((msg) =>
          msg.isTemp && msg.id === newMessage.tempId
            ? { ...newMessage, isTemp: false }
            : msg
        );
      } else if (isOurMessage) {
        // Fallback: remove temp message by content (legacy, should rarely happen)
        const filtered = prev.filter(
          (msg) => !(msg.isTemp && msg.message === newMessage.message)
        );
        return [...filtered, newMessage];
      } else {
        if (prev.some((msg) => msg.id === newMessage.id)) return prev;
        return [...prev, newMessage];
      }
    });
  }

      // Update contacts list (this should happen for all messages)
      setContacts((prev) => {
        const updatedUsers = [...prev.users];
        let contactIndex = updatedUsers.findIndex(
          (user) => user.id === partnerId
        );

        // If contact doesn't exist and we have sender details, create new contact
        if (contactIndex === -1 && newMessage.senderDetails && !isOurMessage) {
          const newContact = {
            id: partnerId,
            name:
              newMessage.senderDetails.firstName +
              " " +
              (newMessage.senderDetails.familyName || ""),
            image: newMessage.senderDetails.image,
            isOnline: true,
            lastMessageTime: newMessage.created_at,
            lastMessage: {
              id: newMessage.id,
              message: newMessage.message,
              sender_id: newMessage.sender_id,
              sender_type: newMessage.sender_type,
              created_at: newMessage.created_at,
            },
            // unreadMessages: isForCurrentUser && !isForActiveChat ? 1 : 0,
          };
          updatedUsers.unshift(newContact);
          contactIndex = 0;
        }
        // If contact exists, update it
        else if (contactIndex !== -1) {
          updatedUsers[contactIndex] = {
            ...updatedUsers[contactIndex],
            lastMessageTime: newMessage.created_at,
            lastMessage: {
              id: newMessage.id,
              message: newMessage.message,
              sender_id: newMessage.sender_id,
              sender_type: newMessage.sender_type,
              created_at: newMessage.created_at,
            },
            // unreadMessages:
            //   isForCurrentUser && !isForActiveChat
            //     ? (updatedUsers[contactIndex].unreadMessages || 0) + 1
            //     : updatedUsers[contactIndex].unreadMessages,
          };

          // Move to top if not already there
          if (contactIndex > 0) {
            const [contact] = updatedUsers.splice(contactIndex, 1);
            updatedUsers.unshift(contact);
          }
        }

        return { users: updatedUsers };
      });

      // Update unread counts - only for messages not in active chat
      if (!isOurMessage && !isForActiveChat) {
        setUnreadMessages((prev) => ({
          ...prev,
          [partnerId]: (prev[partnerId] || 0) + 1,
        }));
      }

      // If this is the active chat, mark as read and scroll to bottom
      if (isForActiveChat && !isOurMessage) {
        socket.emit("mark_messages_read", {
          lang,
          sender_id: partnerId,
          sender_type: newMessage.sender_type,
        });
        scrollToBottom();
      }
    });

    socket.on("messages_read", ({ sender_id, receiver_id }) => {
      if (parsedUser.id === receiver_id) {
        setUnreadMessages((prev) => {
          const newUnread = { ...prev };
          delete newUnread[sender_id];
          return newUnread;
        });
      }
    });

    socket.on(
      "chat_history",
      ({ messages: newMessages, total, totalPages, currentPage }) => {
        // Reverse the order of new messages before adding them to state
        const orderedMessages = [...newMessages].reverse();

        setMessages((prev) => {
          // For the first page, replace all messages with the reversed order
          if (currentPage === 1) {
            return orderedMessages;
          }
          // For subsequent pages, append the new (older) messages at the beginning
          return [...orderedMessages, ...prev];
        });

        setTotalMessagePages(totalPages);
        setCurrentMessagePage(currentPage);
        setHasMoreMessages(currentPage < totalPages);
        setLoadingMessages(false);

        if (currentPage === 1) {
          scrollToBottom();
        } else {
          // Maintain scroll position after loading older messages
          const scrollHeightBefore = chatBodyRef.current.scrollHeight;
          setTimeout(() => {
            chatBodyRef.current.scrollTop =
              chatBodyRef.current.scrollHeight - scrollHeightBefore;
          }, 0);
        }
      }
    );

    socket.on("typing", ({ sender_id, isTyping }) => {
      console.log("Typing event received:", sender_id, isTyping);
      if (activeContact && sender_id === activeContact.id) {
        setOtherTyping(isTyping);

        // Clear typing indicator after 3 seconds if no follow-up event
        clearTimeout(typingTimeoutRef.current);
        if (isTyping) {
          typingTimeoutRef.current = setTimeout(() => {
            setOtherTyping(false);
          }, 3000);
        }
      }
    });

    // Message status updates
    socket.on("message_delivered", ({ message_id }) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === message_id ? { ...msg, is_delivered: true } : msg
        )
      );
    });

    socket.on("message_read", ({ message_id }) => {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === message_id ? { ...msg, is_read: true } : msg
        )
      );
    });

    socket.on("get_customer_by_order_id", ({ customer_id }) => {
      console.log("Received get_customer_by_order_id event for customer:", customer_id);
      
      // Use setContacts to access current state and check if customer exists
      setContacts((currentContacts) => {
        const customer = currentContacts.users.find(user => user.id === customer_id);
        
        if (customer) {
          // Directly select the customer (use setTimeout to avoid React state update conflicts)
          setTimeout(() => handleSelectContact(customer), 0);
          console.log("Auto-selected customer:", customer.name);
        } else {
          // Set pending customer ID and fetch updated users
          console.log("Customer not found in current list, setting as pending and fetching users...");
          setPendingCustomerId(customer_id);
          setTimeout(() => fetchUsers(1), 0);
        }
        
        return currentContacts; // Return unchanged state
      });
    });

    // Listen for customer details by order ID response
    socket.on("customer_by_order_id", (customerDetails) => {
      console.log("Received customer details for order:", customerDetails);
      
      // Create the customer contact object
      const customerContact = {
        id: customerDetails.id,
        name: customerDetails.firstName,
        image: customerDetails.image,
        isOnline: customerDetails.isOnline,
        type: 'user'
      };

      // Set the customer as the active contact
      setActiveContact(customerContact);

      // Add customer to contacts list if not already there
      setContacts((prev) => {
        console.log("Before adding customer - current contacts:", prev.users);
        const existingContactIndex = prev.users.findIndex(
          (user) => user.id === customerDetails.id
        );
        
        if (existingContactIndex === -1) {
          // Add new contact to the beginning of the list
          const newContact = {
            id: customerDetails.id,
            name: customerDetails.firstName,
            image: customerDetails.image,
            isOnline: customerDetails.isOnline,
            lastMessage: null,
            lastMessageTime: null,
            type: 'user'
          };
          
          console.log("Adding new customer contact:", newContact);
          const newContactsList = {
            users: [newContact, ...prev.users]
          };
          console.log("New contacts list:", newContactsList);
          
          // Update hasContacts to true since we now have at least one contact
          setHasContacts(true);
          
          return newContactsList;
        } else {
          // Update existing contact
          const updatedUsers = [...prev.users];
          updatedUsers[existingContactIndex] = {
            ...updatedUsers[existingContactIndex],
            name: customerDetails.firstName,
            image: customerDetails.image,
            isOnline: customerDetails.isOnline,
          };
          
          console.log("Updated existing customer contact");
          
          // Ensure hasContacts is true
          setHasContacts(true);
          
          return { users: updatedUsers };
        }
      });


      // Load chat messages for this customer (similar to handleSelectContact)
      setMessages([]);
      setCurrentMessagePage(1);
      setHasMoreMessages(true);
      setOtherTyping(false);

      // Emit to get messages for this customer
      socket.emit("get_messages", {
        lang,
        receiver_id: customerDetails.id,
        receiver_type: 'user', // Since this is a customer/user
        page: 1,
        limit: 10,
      });

      // Mark messages as read
      socket.emit("mark_messages_read", {
        lang,
        sender_id: customerDetails.id,
        sender_type: 'user',
      });
    });

    // Listen for customer error response
    socket.on("customer_error", (error) => {
      console.error("Customer fetch error:", error);
      toast.error(`Failed to get customer details: ${error.error || "Unknown error"}`);
    });

    socket.on("reconnect", () => {
      socket.emit("venue_owner_join", { lang });
      fetchUsers(currentContactPage);
    });

    return () => {
      socket.off("users");
      socket.off("receive_message");
      socket.off("chat_history");
      socket.off("user_online");
      socket.off("messages_read");
      socket.off("typing");
      socket.off("message_delivered");
      socket.off("message_read");
      socket.off("get_customer_by_order_id");
      socket.off("customer_by_order_id");
      socket.off("customer_error");
      socket.off("reconnect");
    };
  }, [socket, activeContact, currentContactPage]);

  const fetchUsers = (page) => {
    console.log("fetching users");
    if (socket) {
      socket.emit("get_customers", {
        lang,
        page,
        limit: 10,
      });
    }
    // check failed get users
    socket.on("users_error", (error) => {
      setLoading(false);
      console.error("Failed to fetch users:", error);
    });
  };

  const handleSelectContact = (contact) => {
    if (!contact || !contact.id) return;
    const unreadCount = unreadMessages[contact.id] || 0;


    // Use a single state update for better performance
    setContacts((prev) => {
      const updatedUsers = prev.users.map((user) =>
        user.id === contact.id ? { ...user, unreadMessages: 0 } : user
      );

      return { users: updatedUsers };
    });

    // Clear from unreadMessages state
    setUnreadMessages((prev) => {
      const newUnread = { ...prev };
      delete newUnread[contact.id];
      return newUnread;
    });

    if (unreadCount > 0) {
      window.dispatchEvent(
        new CustomEvent("messagesRead", {
          detail: {
            senderId: contact.id,
            unreadCount: unreadCount,
          },
        })
      );
    }

    setActiveContact(contact);
    setMessages([]);
    setCurrentMessagePage(1);
    setHasMoreMessages(true);
    setOtherTyping(false);

    if (socket) {
      socket.emit("get_messages", {
        lang,
        receiver_id: contact.id,
        receiver_type: contact.type,
        page: 1,
        limit: 10,
      });

      socket.emit("mark_messages_read", {
        lang,
        sender_id: contact.id,
        sender_type: contact.type,
      });
    }
  };

  const loadMoreMessages = () => {
    if (loadingMessages || !hasMoreMessages || !socket || !activeContact)
      return;

    setLoadingMessages(true);
    socket.emit("get_messages", {
      lang,
      receiver_id: activeContact.id,
      receiver_type: activeContact.type,
      page: currentMessagePage + 1,
      limit: 10,
    });
  };

  const handleScroll = () => {
    if (chatBodyRef.current) {
      const { scrollTop } = chatBodyRef.current;
      if (scrollTop === 0 && hasMoreMessages) {
        loadMoreMessages();
      }
    }
  };

  const handleTyping = (isTyping) => {
    if (!socket || !activeContact) return;

    clearTimeout(typingTimeoutRef.current);

    if (isTyping) {
      socket.emit("typing", {
        lang,
        receiver_id: activeContact.id,
        receiver_type: activeContact.type,
        isTyping: true,
      });

      typingTimeoutRef.current = setTimeout(() => {
        socket.emit("typing", {
          lang,
          receiver_id: activeContact.id,
          receiver_type: activeContact.type,
          isTyping: false,
        });
        setIsTyping(false);
      }, 3000);
    } else {
      socket.emit("typing", {
        lang,
        receiver_id: activeContact.id,
        receiver_type: activeContact.type,
        isTyping: false,
      });
    }

    setIsTyping(isTyping);
  };

  const handleSendMessage = async () => {
    if ((!message.trim() && !selectedFile) || !socket || !activeContact || isUploading) return;

    let uploadedFilename = null;

    // First, upload file if selected
    if (selectedFile) {
      try {
        // Set uploading state
        setIsUploading(true);
        
        // Validate file size (10MB limit)
        const maxSize = 10 * 1024 * 1024; // 10MB
        if (selectedFile.size > maxSize) {
          toast.error("File too large. Maximum size is 10MB.");
          setIsUploading(false);
          return;
        }

        console.log("Uploading file:", selectedFile.name, selectedFile.type);
        uploadedFilename = await uploadFile(selectedFile);
        console.log("File uploaded successfully:", uploadedFilename);
      } catch (error) {
        console.error("File upload failed:", error);
        toast.error(`Failed to upload file: ${error.message}`);
        setIsUploading(false);
        return; // Don't send message if file upload fails
      } finally {
        setIsUploading(false);
      }
    }

    const tempId = `temp-${Date.now()}`;
    const tempMessage = {
      id: tempId,
      sender_id: parsedUser?.id,
      receiver_id: activeContact.id,
      sender_type: "venue_owner",
      message,
      created_at: new Date().toISOString(),
      is_delivered: false,
      is_read: false,
      isTemp: true,
      file: uploadedFilename, // Use uploaded filename
      senderDetails: {
        id: parsedUser?.id,
        firstName: parsedUser?.firstName,
        familyName: parsedUser?.familyName,
      },
    };

    // Optimistically update UI
    setMessages((prev) => [...prev, tempMessage]);
    const messageToSend = message;
    const fileToSend = uploadedFilename;
    
    setMessage("");
    setSelectedFile(null);
    // Clear file input
    const fileInput = document.getElementById("fileInput");
    if (fileInput) fileInput.value = "";

    // Update contacts list immediately
    setContacts((prev) => {
      const updatedUsers = [...prev.users];
      const contactIndex = updatedUsers.findIndex(
        (u) => u.id === activeContact.id
      );

      if (contactIndex !== -1) {
        updatedUsers[contactIndex] = {
          ...updatedUsers[contactIndex],
          lastMessageTime: tempMessage.created_at,
          lastMessage: {
            id: tempId,
            message: tempMessage.message,
            sender_id: tempMessage.sender_id,
            sender_type: tempMessage.sender_type,
            created_at: tempMessage.created_at,
          },
        };

        // Move to top
        if (contactIndex > 0) {
          const [contact] = updatedUsers.splice(contactIndex, 1);
          updatedUsers.unshift(contact);
        }
      }

      return { users: updatedUsers };
    });

    // Send to server with filename reference
    socket.emit("venue_message", {
      lang,
      receiver_id: activeContact.id,
      message: messageToSend,
      filename: fileToSend, // Send the uploaded filename
      tempId, // Include tempId for reconciliation
    });

    scrollToBottom();
    handleTyping(false);
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, 100);
  };

  const filteredContacts = () => {
    const filter = (items) =>
      items?.filter(
        (item) =>
          item?.name?.toLowerCase().includes(searchTerm.toLowerCase()) 
      );

    console.log("Filtering contacts with searchTerm:", searchTerm);
    console.log("Raw contacts.users:", contacts.users);
    const filtered = {
      users: filter(contacts.users || []),
    };
    console.log("Filtered contacts result:", filtered);
    console.log("Filtered contacts users:", filtered.users);
    console.log("Filtered users length:", filtered.users?.length);
    return filtered;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    // Get day/month/year
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    // Get hours and minutes
    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");

    // Convert to 12-hour format and determine AM/PM
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12;

    return `${day}/${month}/${year} - ${hours}:${minutes} ${ampm}`;
  };

  const getInitials = (name) => {
    if (!name) return "";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  };

  const renderMessageStatus = (message) => {
    if (message.sender_id !== parsedUser.id) return null;

    if (message.is_read) {
      return (
        <MessageStatus sent>
          Read • {formatTimestamp(message.created_at)}
        </MessageStatus>
      );
    }
    if (message.is_delivered) {
      return (
        <MessageStatus sent>
          Delivered • {formatTimestamp(message.created_at)}
        </MessageStatus>
      );
    }
    return (
      <MessageStatus sent>
        Sent • {formatTimestamp(message.created_at)}
      </MessageStatus>
    );
  };

  const filtered = filteredContacts();

  console.log("Current activeContact:", activeContact);
  console.log("Filtered contacts for rendering:", filtered);
  console.log("hasContacts state:", hasContacts);

  const finalDisplayedName = parsedUser
    ? `${parsedUser.firstName} ${parsedUser.familyName}`
    : "You: ";
  console.log("Final displayed name:", parsedUser);
  const finalDisplayedImage =
    (parsedUser && parsedUser.image) || "/default-user.png";
      console.log("current contacts after adding customer:", contacts.users);

  if (loading) {
    return (
      <div className="loader-container">
        <Loader />
      </div>
    );
  }
  console.log("Filtered contacts for rendering:", filtered.users,hasContacts);

  return (
    <div className="chat-container">
      <h2 className="chat-maintitle">{t("chat.title")}</h2>
      <h3 className="chat-subtitle">{t("chat.subtitle")}</h3>
      {hasContacts ? (
        <ChatContainer>
          <Sidebar className="chat-sidebar">
            <div className="chat-search">
              <img
                className="organizer-search-icon"
                src="/search-normal.svg"
                alt=""
                onClick={() => setSearchTerm("")}
                style={{ cursor: "pointer" }}
              />
              <SearchBox
                placeholder={t("chat.searchPlaceholder")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <ContactsList>
              {console.log("About to check filtered.users.length:", filtered.users?.length)}
              {console.log("filtered.users:", filtered.users)}
              {filtered.users.length > 0 && (
                <>
                  <div
                    style={{
                      padding: "10px 20px",
                      color: "#6b7280",
                      fontSize: "12px",
                      fontWeight: "bold",
                    }}
                  ></div>
                  {console.log("Inside filtered.users.length > 0 condition")}
                  {filtered.users.length > 0 ? (
                    filtered.users.map((user) => {
                      console.log("Rendering user in contact list:", user);
                      const isActive = activeContact?.id === user.id && activeContact?.type === "user";
                      console.log(`Rendering contact ${user.name} (ID: ${user.id}), active: ${isActive}`);
                      console.log(`ActiveContact ID: ${activeContact?.id}, Type: ${activeContact?.type}`);
                      console.log(`User ID: ${user.id}, Type: ${user.type}`);
                      
                      return (
                        <ContactItem
                          key={`user-${user.id}`}
                          active={isActive}
                          onClick={() => handleSelectContact(user)}
                        >
                        <ContactInfo>
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              marginBottom: "12px",
                            }}
                          >
                            <ContactName>
                              {user.name} 
                            </ContactName>
                            <div style={{ fontSize: "0.7rem", color: "#666" }}>
                              {user.lastMessageTime
                                ? formatTimestamp(user.lastMessageTime)
                                : ""}
                            </div>
                          </div>
                          <div
                            style={{
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "200px",
                              fontSize: "0.8rem",
                              color: "#666",
                            }}
                          >
                            {user.lastMessage ? (
                              <>
                                {user.lastMessage?.sender_id ===
                                parsedUser.id && 
     user?.lastMessage?.sender_type === "venue_owner" ? (
                                  <span style={{ fontStyle: "italic" }}>
                                    You:{" "}
                                  </span>
                                ) : null}
                                {user.lastMessage?.message?.length > 33
                                  ? `${user.lastMessage.message.substring(
                                      0,
                                      30
                                    )}...`
                                  : user.lastMessage?.message}
                              </>
                            ) : (
                              "No messages yet"
                            )}
                          </div>
                        </ContactInfo>
                        {(unreadMessages[user.id] > 0) && (
                          <div className="unread-indicator">
                            {unreadMessages[user.id] || user.unreadMessages}
                          </div>
                        )}
                      </ContactItem>
                      );
                    })
                  ) : (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#666",
                        fontStyle: "italic",
                      }}
                    >
                      {searchTerm
                        ? t("chat.noMatchingContacts")
                        : t("chat.noContacts")}
                    </div>
                  )}
                </>
              )}
              {filtered.users?.length === 0 && (
                <div
                  style={{
                    padding: "20px",
                    textAlign: "center",
                    color: "#6b7280",
                  }}
                >
                  {t("chat.noUsers")}
                </div>
              )}
            </ContactsList>
            <PaginationControls>
              <Button
                onClick={() => {
                  const prevPage = currentContactPage - 1;
                  setCurrentContactPage(prevPage);
                  fetchUsers(prevPage);
                }}
                disabled={currentContactPage === 1}
              >
                {t("chat.pagination.previous")}
              </Button>
              <span
                style={{
                  fontFamily: "Tahoma, Arial, sans-serif",
                  unicodeBidi: "plaintext",
                }}
                dir={lang === "ar" ? "rtl" : "ltr"}
              >
                {t("chat.pagination.page")} {currentContactPage}{" "}
                {t("chat.pagination.of")} {totalContactPages}
              </span>
              <Button
                onClick={() => {
                  const nextPage = currentContactPage + 1;
                  setCurrentContactPage(nextPage);
                  fetchUsers(nextPage);
                }}
                disabled={currentContactPage === totalContactPages}
              >
                {t("chat.pagination.next")}
              </Button>
            </PaginationControls>
          </Sidebar>

          <ChatArea>
            {activeContact ? (
              <>
                {/* Chat Header with active contact info */}
                <div style={{
                  padding: "16px 20px",
                  borderBottom: "1px solid #e0e0e0",
                  backgroundColor: "#f8f9fa",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px"
                }}>
                  <img 
                    src={activeContact.image || "/user-profile.png"} 
                    alt={activeContact.name}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      objectFit: "cover"
                    }}
                  />
                  <div>
                    <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "600", color: "#333" }}>
                      {activeContact.name}
                    </h3>
                    <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>
                      {activeContact.isOnline ? "Online" : "Offline"}
                    </p>
                  </div>
                </div>
                
                <ChatBody ref={chatBodyRef} onScroll={handleScroll}>
                  <h3 className="chat-title">{t("chat.messageDetails")}</h3>
                  {loadingMessages && (
                    <div
                      style={{
                        textAlign: "center",
                        padding: "10px",
                        color: "#888",
                      }}
                    ></div>
                  )}
                  {messages.length === 0 ? (
                    <EmptyChatState>
                      <div style={{ fontSize: "20px", marginBottom: "10px" }}>
                        {t("chat.noMessages")}
                      </div>
                      <div>
                        {t("chat.startConversation")} {activeContact?.name}
                      </div>
                    </EmptyChatState>
                  ) : (
                    <>
                      {messages.map((msg, index) => {
                        const isSent = msg.sender_id === parsedUser.id && msg.sender_type === "venue_owner";
                        const senderAvatar = isSent
                          ? finalDisplayedImage
                          : activeContact.image;
                        const senderInitials = isSent
                          ? getInitials(
                              `${parsedUser.firstName} ${parsedUser.familyName}`
                            )
                          : getInitials(
                              `${activeContact.name}` 
                            );

                        return (
                          <MessageWrapper key={msg.id || index} sent={isSent}>
                            <MessageBubble sent={isSent}>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  flexDirection: isSent ? "row-reverse" : "row",
                                }}
                              >
                                <AvatarWrapper>
                                  <Avatar image={senderAvatar}>
                                    {!senderAvatar && senderInitials}
                                  </Avatar>
                                </AvatarWrapper>

                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    width: "100%",
                                    flexDirection: isSent
                                      ? "row-reverse"
                                      : "row",
                                  }}
                                >
                                  <h3 style={{ margin: "0 15px" }}>
                                    {isSent
                                      ? finalDisplayedName
                                      : `${activeContact.name}`}
                                  </h3>
                                  <div
                                    style={{
                                      fontSize: "0.7rem",
                                      color: "#666",
                                    }}
                                  >
                                    {formatDate(msg.created_at)}
                                  </div>
                                </div>
                              </div>

                              <p
                                className="chat-message"
                                style={{
                                  textAlign: isSent ? "right" : "left",
                                  marginTop: "8px",
                                  marginBottom: "4px",
                                }}
                              >
                                {msg.message}
                              </p>

                              {/* File attachment display */}
                              {msg.file && (
                                <FilePreview style={{
                                  marginTop: "8px",
                                  marginBottom: "4px",
                                  textAlign: isSent ? "right" : "left",
                                }}>
                                  <div
                                    style={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      cursor: "pointer",
                                      padding: "8px 12px",
                                      border: "1px solid #e0e0e0",
                                      borderRadius: "8px",
                                      backgroundColor: "#f9f9f9",
                                      maxWidth: "200px",
                                    }}
                                    onClick={() => {
                                      const link = document.createElement("a");
                                      link.href = msg.file_url;
                                      link.download = msg.file;
                                      link.target = "_blank";
                                      document.body.appendChild(link);
                                      link.click();
                                      document.body.removeChild(link);
                                    }}
                                  >
                                    <span style={{ marginRight: "8px", fontSize: "16px" }}>
                                      {getFileIcon(msg.file)}
                                    </span>
                                    <div style={{ textAlign: "left" }}>
                                      <div
                                        style={{
                                          fontSize: "12px",
                                          fontWeight: "500",
                                          color: "#333",
                                          overflow: "hidden",
                                          textOverflow: "ellipsis",
                                          whiteSpace: "nowrap",
                                          maxWidth: "120px",
                                        }}
                                      >
                                        {msg.file}
                                      </div>
                                      <div
                                        style={{
                                          fontSize: "10px",
                                          color: "#666",
                                        }}
                                      >
                                        {t("chat.clickToOpen")}
                                      </div>
                                    </div>
                                  </div>
                                </FilePreview>
                              )}

                              {renderMessageStatus(msg)}
                            </MessageBubble>
                          </MessageWrapper>
                        );
                      })}
                    </>
                  )}
                  <div ref={messagesEndRef} />
                </ChatBody>
                <ChatFooter>
                  <div className="message-input">
                    {/* File input (hidden) */}
                    <input
                      type="file"
                      id="fileInput"
                      style={{ display: "none" }}
                      disabled={isUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          const maxSize = 10 * 1024 * 1024; // 10MB
                          if (file.size > maxSize) {
                            toast.error("File too large. Maximum size is 10MB.");
                            e.target.value = "";
                            return;
                          }
                          setSelectedFile(file);
                        }
                      }}
                    />
                    
                    {/* File button */}
                    <FileButton
                      onClick={() => !isUploading && document.getElementById("fileInput")?.click()}
                      title={isUploading ? "Uploading..." : "Attach file"}
                      style={{
                        opacity: isUploading ? 0.5 : 1,
                        cursor: isUploading ? "not-allowed" : "pointer"
                      }}
                    >
                      {isUploading ? "⏳" : "📎"}
                    </FileButton>

                    {/* File preview */}
                    {selectedFile && (
                      <FilePreview style={{ margin: "0 8px" }}>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "4px 8px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "4px",
                            backgroundColor: isUploading ? "#e3f2fd" : "#f0f0f0",
                            fontSize: "12px",
                            opacity: isUploading ? 0.8 : 1,
                          }}
                        >
                          <span style={{ marginRight: "6px" }}>
                            {isUploading ? (
                              <SmallSpinner />
                            ) : (
                              getFileIcon(selectedFile.name)
                            )}
                          </span>
                          <span style={{ marginRight: "6px", maxWidth: "100px", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {selectedFile.name}
                          </span>
                          <span style={{ fontSize: "10px", color: "#666", marginRight: "6px" }}>
                            {isUploading ? "Uploading..." : `(${formatFileSize(selectedFile.size)})`}
                          </span>
                          <button
                            onClick={() => {
                              if (!isUploading) {
                                setSelectedFile(null);
                                const fileInput = document.getElementById("fileInput");
                                if (fileInput) fileInput.value = "";
                              }
                            }}
                            disabled={isUploading}
                            style={{
                              background: "none",
                              border: "none",
                              cursor: isUploading ? "not-allowed" : "pointer",
                              color: isUploading ? "#ccc" : "#999",
                              fontSize: "14px",
                              opacity: isUploading ? 0.5 : 1,
                            }}
                          >
                            ×
                          </button>
                        </div>
                      </FilePreview>
                    )}
                    
                    <SendButton
                      onClick={handleSendMessage}
                      disabled={(!message.trim() && !selectedFile) || !activeContact || isUploading}
                    >
                      {isUploading ? (
                        <SendButtonSpinner />
                      ) : (
                        <img src="/send-2.svg" alt="send icon" />
                      )}
                    </SendButton>
                    <MessageInput
                      value={message}
                      onChange={(e) => {
                        setMessage(e.target.value);
                        if (e.target.value.length > 0) {
                          handleTyping(true);
                        } else {
                          handleTyping(false);
                        }
                      }}
                      onKeyPress={(e) =>
                        e.key === "Enter" && handleSendMessage()
                      }
                    />
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowPicker((prev) => !prev);
                    }}
                    style={{
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      fontSize: "1.2rem",
                      margin: "0 10px",
                    }}
                    aria-label="Open emoji picker"
                  >
                  🙂
                  </button>

                  {showPicker && (
                    <div
                      className="emojy-picker"
                      style={{
                        position: "absolute",
                        bottom: "70px",
                        right: "10px",
                        zIndex: 1000,
                      }}
                    >
                      <Picker
                        onEmojiClick={(emojiObject) => {
                          setMessage((prev) => prev + emojiObject.emoji);
                          setShowPicker(false);
                        }}
                      />
                    </div>
                  )}
                </ChatFooter>
              </>
            ) : (
              <EmptyChatState>
                <svg
                  width="100"
                  height="100"
                  viewBox="0 0 24 24"
                  fill="#e1e4e8"
                >
                  <path d="M21 4H3C1.9 4 1 4.9 1 6V18C1 19.1 1.9 20 3 20H21C22.1 20 23 19.1 23 18V6C23 4.9 22.1 4 21 4Z" />
                  <path
                    d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12Z"
                    fill="#f0f2f5"
                  />
                  <path
                    d="M12 14C8.13 14 5 17.13 5 21H19C19 17.13 15.87 14 12 14Z"
                    fill="#f0f2f5"
                  />
                </svg>
                <div style={{ marginTop: "15px", fontSize: "18px" }}>
                  {t("chat.selectContact")}
                </div>
              </EmptyChatState>
            )}
          </ChatArea>
        </ChatContainer>
      ) : (
        <div className="empty-chat">
          <img src="/empty-state.svg" alt="messages empty" />
          <h4>{t("chat.noMessagesState")}</h4>
        </div>
      )}
    </div>
  );
};

export default VenueOwnerChatSystem;
